import { ColumnDef } from '@tanstack/react-table'
import { ArrowRight } from 'lucide-react'

import { GetMemberEventLogTableData } from '@/modules/manage-member/type'
import { Badge } from '@/common/ui/badge'
import { Button } from '@/common/ui/button'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@/common/ui/dialog'
import { tagEventMapper } from '@/modules/manage-member/mappers'
import { OperatorEventHistoryStatus } from '@/schema'

export const columns: ColumnDef<GetMemberEventLogTableData>[] = [
	{
		accessorKey: 'operatorName',
		header: 'ผู้ดูแลระบบ',
	},
	{
		accessorKey: 'eventName',
		header: 'ชื่อการทำรายการ',
	},
	{
		accessorKey: 'description',
		header: 'รายละเอียด',
		cell: ({ row }) => {
			const description = row.original.description
			const maxLength = 50
			const isTruncated = description.length > maxLength

			// Format description: split by commas but keep the first part (before |) intact
			const formatDescription = (text: string) => {
				// Check if there's a separator (|) to split header from fields
				const separatorIndex = text.indexOf('|')
				if (separatorIndex === -1) {
					// No separator, just highlight arrows in the text
					return text.split('→').map((part, index, array) => (
						<span key={index}>
							{part}
							{index < array.length - 1 && (
								<ArrowRight className="mx-1 inline-block h-5 w-5 font-bold text-red-600" />
							)}
						</span>
					))
				}

				const header = text.slice(0, separatorIndex + 1).trim()
				const fields = text.slice(separatorIndex + 1).trim()

				// Split by comma and format each field on new line
				const fieldsList = fields
					.split(',')
					.map((field) => field.trim())
					.filter((field) => field.length > 0)

				return (
					<div>
						<div>{header}</div>
						{fieldsList.map((field, index) => {
							// Split each field by arrow and highlight it
							const parts = field.split('→')
							return (
								<div key={index}>
									{parts.map((part, partIndex) => (
										<span key={partIndex}>
											{part}
											{partIndex < parts.length - 1 && (
												<ArrowRight className="mx-1 inline-block h-5 w-5 font-bold text-red-600" />
											)}
										</span>
									))}
								</div>
							)
						})}
					</div>
				)
			}

			const formattedDescription = formatDescription(description)

			return (
				<div className="flex items-center gap-2">
					<span className="line-clamp-2">
						{isTruncated
							? `${description.slice(0, maxLength)}...`
							: description}
					</span>
					{isTruncated && (
						<Dialog>
							<DialogTrigger asChild>
								<Button variant="link" className="h-auto p-0 text-blue-600">
									อ่านเพิ่มเติม
								</Button>
							</DialogTrigger>
							<DialogContent className="max-h-[80vh] max-w-3xl">
								<DialogHeader>
									<DialogTitle>รายละเอียดการทำรายการ</DialogTitle>
								</DialogHeader>
								<div className="max-h-[60vh] overflow-y-auto overflow-x-hidden">
									<div className="break-all pr-4 text-sm text-foreground">
										{formattedDescription}
									</div>
								</div>
							</DialogContent>
						</Dialog>
					)}
				</div>
			)
		},
	},
	{
		accessorKey: 'status',
		header: 'สถานะ',
		cell: ({ row }) => {
			const status = row.original.status
			const isImportant = status === OperatorEventHistoryStatus.IMPORTANT
			return (
				<Badge
					variant={isImportant ? 'destructive' : 'secondary'}
					className={isImportant ? 'font-semibold' : ''}
				>
					{status === OperatorEventHistoryStatus.IMPORTANT ? 'สำคัญ' : 'ปกติ'}
				</Badge>
			)
		},
	},

	{
		accessorKey: 'tagEvent',
		header: 'TAG',
		cell: ({ row }) => {
			return (
				<Badge>
					{row.original.tagEvent ? tagEventMapper[row.original.tagEvent] : ''}
				</Badge>
			)
		},
	},
	{
		accessorKey: 'ipAddress',
		header: 'IP Address',
		cell: ({ row }) => row.original.ipAddress ?? '-',
	},
	{
		accessorKey: 'createdAt',
		header: 'วันที่ทำรายการ',
		cell: ({ row }) => new Date(row.original.createdAt).toLocaleString(),
	},
]
